/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: brandingApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client functions for Branding module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { BrandingConfig } from '../types';

export interface Tenant {
  id: number;
  uuid?: string;  // External UUID (replaces Sqids)
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantListResponse {
  data: Tenant[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface BrandingResponse {
  tenant: Tenant;
  brandingConfig: BrandingConfig;
}

export interface UploadResponse {
  success: boolean;
  path: string;
}

export async function fetchTenants(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}, tenantSlug: string, userId?: number): Promise<TenantListResponse> {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    searchParams.set('search', params.search);
  }

  const response = await fetch(
    `${buildApiUrl('/api/branding/tenants')}?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => response.statusText);
    throw new Error(message || `HTTP ${response.status}`);
  }

  const payload = await response.json();
  if (!payload || typeof payload !== 'object' || !payload.success) {
    throw new Error('Invalid response format');
  }

  return {
    data: (payload.data ?? payload.tenants ?? []) as Tenant[],
    pagination: payload.pagination ?? {
      page: params.page,
      limit: params.limit,
      total: Array.isArray(payload.data) ? payload.data.length : 0,
      totalPages: 1,
    },
  };
}

export async function fetchTenantBranding(
  tenantId: string,
  tenantSlug: string,
  userId?: number
): Promise<BrandingResponse> {
  const response = await fetch(
    buildApiUrl(`/api/branding/tenants/${tenantId}`),
    {
      headers: getApiHeaders(tenantSlug, userId),
    }
  );

  if (response.status === 404) {
    throw new Error('TENANT_NOT_FOUND');
  }

  if (!response.ok) {
    const message = await response.text().catch(() => response.statusText);
    throw new Error(message || `HTTP ${response.status}`);
  }

  const payload = await response.json();
  if (!payload?.success || !payload.tenant) {
    throw new Error('Invalid branding response');
  }

  return {
    tenant: payload.tenant,
    brandingConfig: payload.brandingConfig,
  };
}

export async function updateBranding(
  tenantId: number,
  config: BrandingConfig,
  tenantSlug: string,
  userId?: number
): Promise<BrandingResponse> {
  const response = await fetch(buildApiUrl(`/api/branding/${tenantId}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error('Save failed');
  }

  const payload = await response.json();
  if (!payload?.success) {
    throw new Error('Save failed');
  }

  return {
    tenant: payload.tenant,
    brandingConfig: payload.brandingConfig,
  };
}

export async function uploadBrandingFile(
  file: File,
  tenantSlug: string,
  userId?: number
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', 'branding');

  const headers = getApiHeaders(tenantSlug, userId);
  delete headers['Content-Type'];

  const response = await fetch(buildApiUrl('/api/branding/upload'), {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Upload failed');
  }

  const payload = await response.json();
  if (!payload?.success || !payload.path) {
    throw new Error('Invalid upload response');
  }

  return {
    success: payload.success,
    path: payload.path,
  };
}

